import { computed, makeObservable, observable, runInAction } from 'mobx';
import { isInputElement } from '../keyboard_shortcuts';
import { AnyDomainConfig, Domain, MutableOf, StateOf } from './base';
import { Clock, CounterClock, MAX_TIMESTAMP } from './clock';
import { AnyDelta, createRevert, createRevertAll } from './deltas';

export interface UndoManager {
  undo(): void;
  canUndo: boolean;
  redo(): void;
  canRedo: boolean;
  /** Creates an change buffer, which can be added to later */
  createBuffer(): ChangeBuffer;
  doPermanently<T>(operation: () => T): T;
}

export interface ChangeBuffer {
  /* Captures any deltas produces by operation, storing them in this change buffer instead of the top of the undo stack */
  run<T>(operation: () => T): T;
  /** If reverting this entry would do anything */
  readonly wouldRevertCauseChange: boolean;
  /**
   * Reverts the change stored in this buffer.
   *
   * This is treated as an action, rather than undoing the change buffer.  I.e.
   * the user can undo reverting, rather than redo undoing.
   **/
  revert(): void;
}

class NoopUndoManager implements UndoManager {
  readonly undo = () => {};
  readonly canUndo = false;
  readonly redo = () => {};
  readonly canRedo = false;
  readonly createBuffer = () => {
    throw new Error('no editing allowed with NoopUndoManager');
  };
  readonly doPermanently = <T>(operation: () => T) => operation();
}

export const NOOP_UNDO_MANANGER = new NoopUndoManager();

export class UndoManagerImpl<C extends AnyDomainConfig> implements UndoManager {
  readonly mutable: MutableOf<C>;

  /** Map of all objects that need to be informed of changes. */
  private readonly connectedObjects = new Map<
    string,
    { mutable: MutableOf<any>; toMutable: (delta: AnyDelta) => void }
  >();
  /**
   * Map of all objects that need to be informed of changes but haven't been
   * created yet. We buffer relevant deltas until they have been registered.
   */
  private waitingForCreate = new Map<string, AnyDelta[]>();

  private isDoingPermanently = 0;
  private overrideConnectionHandler:
    | {
        addToBuffer: (deltas: readonly AnyDelta[]) => void;
        clock: Clock;
      }
    | undefined = undefined;
  private userBuffer: AnyDelta[] = [];

  private readonly undoStack = observable.array<{
    // The set of deltas that were applied
    forwards: readonly AnyDelta[];
  }>([], { deep: false });
  private readonly redoStack = observable.array<{
    // The set of deltas that were undone
    backwards: readonly AnyDelta[];
  }>([], { deep: false });

  constructor(
    domain: Domain<C>,
    initialState: StateOf<C>,
    private readonly clock: Clock = new CounterClock(),
    private readonly sendToServer?: (deltas: readonly AnyDelta[]) => void,
  ) {
    makeObservable(this);
    const self = this;
    this.mutable = domain.create('root', initialState, {
      get clock() {
        return self.overrideConnectionHandler?.clock || self.clock;
      },
      register: (mutable, toMutable) => {
        const id = mutable.oid;
        if (this.connectedObjects.has(id)) {
          throw new Error(`duplicate mutable object: ${id}`);
        }
        this.connectedObjects.set(id, { mutable, toMutable });
        const deltas = this.waitingForCreate.get(id);
        if (deltas != null) {
          this.waitingForCreate.delete(id);
          for (const d of deltas) {
            toMutable(d);
          }
        }
      },
      fromMutable: (...deltas) => {
        // from a local action (i.e. it'll get sent to the server)
        if (this.isDoingPermanently > 0) {
          // skip adding to undo stack
        } else if (this.overrideConnectionHandler) {
          this.overrideConnectionHandler.addToBuffer(deltas);
        } else {
          this.addToUserBuffer(deltas);
        }
        this.applyToMutable(deltas);
        this.sendToServer?.(deltas);
      },
    });
  }

  private applyToMutable(deltas: readonly AnyDelta[]) {
    // If we don't explicitly wrap in an action, mobx will create shorter-lived
    // actions.
    // This will cause the list mutables to update their @computeds
    // after each delta, rather than after all deltas have been applied.
    runInAction(() => {
      for (const delta of deltas) {
        const cxn = this.connectedObjects.get(delta.for);
        if (cxn) {
          cxn.toMutable(delta);
        } else {
          // we handle deltas in any order
          // we might get an edit for an object that hasn't been created yet
          const buffer = this.waitingForCreate.get(delta.for) || [];
          buffer.push(delta);
          this.waitingForCreate.set(delta.for, buffer);
        }
      }
    });
  }

  private revertAll(deltas: readonly AnyDelta[]) {
    return createRevertAll(this.clock, deltas);
  }

  readonly undo = () => {
    this.stopUserBuffering();
    const entry = this.undoStack.pop();
    if (!entry) return;
    const backwards = this.revertAll(entry.forwards);
    this.applyToMutable(backwards);
    this.sendToServer?.(backwards);
    this.redoStack.push({ backwards });
  };

  readonly redo = () => {
    this.stopUserBuffering();
    const entry = this.redoStack.pop();
    if (!entry) return;
    const forwardsAgain = this.revertAll(entry.backwards);
    this.applyToMutable(forwardsAgain);
    this.sendToServer?.(forwardsAgain);
    this.undoStack.push({ forwards: forwardsAgain });
  };

  get canUndo() {
    return this.undoStack.length > 0;
  }

  get canRedo() {
    return this.redoStack.length > 0;
  }

  @computed
  private get visibleOids() {
    return new Set(this.mutable.visibleOids);
  }

  /** Returns true if reverting this change would make a change to the state */
  private wouldRevertCauseChange(allForwards: readonly AnyDelta[]) {
    for (let i = allForwards.length - 1; i >= 0; i--) {
      const forwards = allForwards[i];
      if (!this.visibleOids.has(forwards.for)) {
        // the object which this delta applies to is no longer in the state
        continue;
      }
      const back = createRevert(MAX_TIMESTAMP, forwards);
      const cxn = this.connectedObjects.get(back.for);
      if (!cxn) {
        // this delta references a object we've never seen
        continue;
      }
      if (cxn.mutable.wouldChange(back)) {
        return true;
      }
    }
    return false;
  }

  readonly doPermanently = <T>(operation: () => T): T => {
    this.isDoingPermanently++;
    try {
      return operation();
    } finally {
      this.isDoingPermanently--;
    }
  };

  readonly createBuffer = (): ChangeBuffer => {
    const moment = this.clock.createMoment();
    const forwards: AnyDelta[] = observable.array([], { deep: false });
    const hasBeenReverted = observable.box(false);
    const wouldRevertCauseChange = computed(() => {
      if (hasBeenReverted.get()) return false;
      if (!this.undoStack.includes(buffer)) return false;
      return this.wouldRevertCauseChange(forwards);
    });

    const buffer: ChangeBuffer & { forwards: AnyDelta[] } = {
      forwards,
      run: <T>(operation: () => T): T => {
        if (this.overrideConnectionHandler) {
          throw new Error('cannot nest undo entries');
        }
        if (hasBeenReverted.get() || !this.undoStack.includes(buffer)) {
          throw new EntryAlreadyUndoneError();
        }
        this.overrideConnectionHandler = {
          addToBuffer: (deltas) => forwards.push(...deltas),
          clock: moment.clock,
        };
        try {
          // batch mobx actions
          return runInAction(() => operation());
        } finally {
          this.overrideConnectionHandler = undefined;
        }
      },
      get wouldRevertCauseChange() {
        return wouldRevertCauseChange.get();
      },
      revert: () => {
        if (!wouldRevertCauseChange.get()) return;
        hasBeenReverted.set(true);
        this.stopUserBuffering();
        const revert = this.revertAll(forwards);
        this.applyToMutable(revert);
        this.sendToServer?.(revert);
        this.undoStack.push({ forwards: revert });
      },
    };
    this.undoStack.push(buffer);
    this.redoStack.clear();
    return buffer;
  };

  private readonly addToUserBuffer = (deltas: readonly AnyDelta[]) => {
    if (deltas.length === 0) return;
    const isStart = this.userBuffer.length === 0;
    this.userBuffer.push(...deltas);

    if (!isStart) return;
    const selectedEl = document.activeElement;
    const maybeStop = () => {
      if (this.userBuffer.length === 0) {
        // we stopped buffering for another reason, e.g. undo/redo
        return;
      }
      if (isInputElement(selectedEl) && document.activeElement === selectedEl) {
        setTimeout(maybeStop, 0);
        // do not stop buffering while they're still editing the same text
        // input as at the start of the buffering session
        return;
      }
      this.stopUserBuffering();
    };
    setTimeout(maybeStop, 0);
  };

  private stopUserBuffering() {
    const deltas = this.userBuffer;
    if (deltas.length === 0) return;
    this.userBuffer = [];
    this.undoStack.push({ forwards: deltas });
    this.redoStack.clear();
  }

  applyFromServer(deltas: readonly AnyDelta[]) {
    this.applyToMutable(deltas);
  }
}

export class EntryAlreadyUndoneError extends Error {
  constructor() {
    super('cannot add to an undone change buffer');
  }
}
